import { getImgOrNoPreview, TProject, TProjectItem } from '@/types/models.ts';
import { routes } from '@/router/routes-urls.ts';
import { cn } from '@/lib/utils.ts';
import { Link } from '@tanstack/react-router';
import { useVisibleImageLoader } from '@/hooks/use-visible-image-loader.ts';
import { Spinner } from '@/components/app/spinner.tsx';
import { useEffect, useState } from 'react';
import { TLabelValue } from '@/types/misc.ts';
import { SelectInput } from '@/components/form/select-input.tsx';
import { FieldLabel } from '@/components/ui/field.tsx';
import { Button } from '@/components/ui/button.tsx';
import { PlusIcon } from 'lucide-react';
import { AppTooltip } from '@/components/app/app-tooltip.tsx';
import { AddNewArea } from '@/app/account/projects/add-new-area.tsx';
import { useSaveProjectAreaMutation } from '@/app/account/api/queries.ts';

function getColorName(colorName?: string, productName?: string) {
  if (!colorName || !productName) {
    return null;
  }
  if (colorName.toLowerCase() === productName.toLowerCase()) {
    return null;
  }
  return <h4 className="leading-none">- {colorName}</h4>;
}

export function ProjectItem({ project, item, areas }: { project: TProject; item: TProjectItem; areas: TLabelValue[] }) {
  const { elementRef, isVisible, imageLoaded, loadImage } = useVisibleImageLoader();
  const [area, setArea] = useState(item.area || '');
  const [showNewArea, setShowNewArea] = useState(false);
  const saveProjectArea = useSaveProjectAreaMutation();
  useEffect(() => {
    loadImage(getImgOrNoPreview(item.img_url));
  }, [item, isVisible]);

  function onAreaChange(value: string) {
    setArea(value);
    saveProjectArea.mutate({
      project_id: project.id,
      item_id: item.id,
      area: value,
    });
  }

  return (
    <div className="product" ref={elementRef}>
      <div className={cn('image-box', 'no-nav', !imageLoaded && 'loading')}>
        <div
          className={cn(
            'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none z-10',
            imageLoaded ? 'hidden' : 'block',
          )}
        >
          <div className="flex items-center gap-6">
            <Spinner className="size-12 opacity-20" />
          </div>
        </div>
        <Link to={routes.productView(item.product_id, item.color_id)} preload={false} viewTransition={true}>
          <div
            className={cn('cur', imageLoaded && 'fade-in')}
            style={{
              backgroundImage: isVisible ? `url(${getImgOrNoPreview(item.img_url)})` : 'none',
            }}
          ></div>
        </Link>
      </div>
      <div className="flex justify-between px-2 py-2">
        <div className="flex flex-col gap-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <h3 className="text-xl font-medium leading-none">{item.product_name}</h3>
            {getColorName(item.color_name, item.product_name)}
          </div>
          <div className="flex items-center gap-2">
            <FieldLabel>Area:</FieldLabel>
            <SelectInput name="area" value={area} onChange={onAreaChange} options={areas} none={true} />
            <AppTooltip tooltip="Add new area">
              <Button variant="ghost" size="icon-sm" onClick={() => setShowNewArea(true)}>
                <PlusIcon />
              </Button>
            </AppTooltip>
          </div>
        </div>
      </div>
      {showNewArea && (
        <AddNewArea project={project} item={item} onClose={() => setShowNewArea(false)} />
      )}
    </div>
  );
}
